import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiServiceBase } from '@api/abstracts';
import { GeoFeatureCollectionDataGouv, GeolocationSearchQueryParams } from '@components/store-locator/models';
import { Time } from '@shared/constants';
import { DataService } from '@shared/services';
import { GoogleService } from '@shared/services/google';
import { LocalitySearch } from '@shared/services/locality-search/models';
import { splitLocation } from '@shared/services/locality-search/utils';
import { envConfiguredSearchableCountries, objectToHashKey } from '@shared/utils';
import { catchError, forkJoin, from, map, Observable, of, switchMap, take } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class LocalitySearchService extends ApiServiceBase<GeoFeatureCollectionDataGouv> {
  private readonly dataGouvUrl = 'https://data.geopf.fr/geocodage/search';
  private readonly googleService = inject(GoogleService);
  private readonly dataService = inject(DataService);
  private readonly googleCountries = envConfiguredSearchableCountries(this.language.region);

  constructor() {
    super('');
  }

  /**
   * Fetches locality information from the DataGouv API based on provided search query parameters.
   *
   * @param {GeolocationSearchQueryParams} params  - The search query parameters containing geolocation information.
   * @param {boolean} shouldIncludeAddressDetails - Whether the result labels should contain country and zipcode.
   */
  getByDataGouv(
    params: GeolocationSearchQueryParams,
    shouldIncludeAddressDetails = true
  ): Observable<LocalitySearch[] | null> {
    const q = params.q?.trim() ?? '';
    if (q.length < 3) {
      this.logs.warn('LocalitySearchService', 'Query too short for DataGouv', { q });
      return of(null);
    }

    return objectToHashKey({ url: this.dataGouvUrl, params }).pipe(
      switchMap((key) =>
        this.customRequest<GeoFeatureCollectionDataGouv>({
          method: 'GET',
          externalUrl: this.dataGouvUrl,
          cache: { cacheExpirationTime: Time.UNLIMITED, forcedKey: `data_gouv_autocomplete_results::${key}` },
          query: params,
        }).pipe(
          map((res) => {
            const list = res.features;
            if (list.length === 0) {
              this.logs.debug('LocalitySearchService', 'No DataGouv results', { params });
              return null;
            }
            return list.map((f) => ({
              name: shouldIncludeAddressDetails
                ? `${f.properties.label} (${f.properties.postcode}), France`
                : f.properties.label,
              id: f.properties.id,
              coordinates: {
                lat: f.geometry.coordinates[1],
                lng: f.geometry.coordinates[0],
              },
              hasGooglePlaceId: false,
            }));
          }),
          catchError((e) => {
            this.logs.error('LocalitySearchService', 'DataGouv API error', {
              status: (e as HttpErrorResponse)?.status,
            });
            return of(null);
          })
        )
      )
    );
  }

  /**
   * Retrieves locality suggestions from Google Autocomplete based on the provided query parameters.
   *
   * @param {GeolocationSearchQueryParams} params An object containing the search parameters. The `q` property represents the query string for the autocomplete.
   * @param {boolean} shouldIncludeAddressDetails - Whether the result labels should contain country and zipcode for dataGouv API
   */
  getByGoogle(
    params: GeolocationSearchQueryParams,
    shouldIncludeAddressDetails = true
  ): Observable<LocalitySearch[] | null> {
    const queryInput = params.q?.trim() ?? '';

    if (queryInput.length < 3) {
      this.logs.warn('LocalitySearchService', 'Query too short for Google Autocomplete', { q: queryInput });
      return of(null);
    }
    if (this.googleCountries.length === 0) {
      this.logs.warn('LocalitySearchService', 'No countries configured for Google search');
      return of(null);
    }

    return this.dataService.get$(`google_autocomplete::${queryInput}::${shouldIncludeAddressDetails}`, 'CACHE').pipe(
      take(1),
      switchMap((cache) => {
        if (cache) {
          this.logs.debug('LocalitySearchService', `[CACHE] Google query "${queryInput}" retrieved from cache`, {
            muted: true,
          });
          return of(cache as LocalitySearch[] | null);
        } else {
          return forkJoin([this.googleService.waitForApiLoad(), from(google.maps.importLibrary('places'))]).pipe(
            switchMap(([, placesLib]) => {
              const { AutocompleteSessionToken, AutocompleteSuggestion } = placesLib;
              const token = new AutocompleteSessionToken();
              const request: google.maps.places.AutocompleteRequest = {
                input: queryInput,
                includedPrimaryTypes: ['locality', 'postal_code'],
                includedRegionCodes: this.googleCountries,
                language: this.language.locale,
                sessionToken: token,
              };
              return from(AutocompleteSuggestion.fetchAutocompleteSuggestions(request));
            }),
            map((response: { suggestions: google.maps.places.AutocompleteSuggestion[] }) => {
              this.logs.debug('LocalitySearchService', 'Google Autocomplete API Response', response);
              const predictions = response.suggestions;

              if (!predictions || predictions.length === 0) {
                this.logs.debug('LocalitySearchService', 'No Google Autocomplete results for query', {
                  query: queryInput,
                });
                return null;
              }
              const localitySearchResults: LocalitySearch[] = predictions.map((prediction) => {
                const place = prediction.placePrediction!;
                const name = splitLocation(place.text?.text);
                return {
                  id: place.placeId,
                  name: shouldIncludeAddressDetails ? name.display : name.city,
                  hasGooglePlaceId: true,
                };
              });

              this.dataService
                .set(
                  `google_autocomplete::${queryInput}::${shouldIncludeAddressDetails}`,
                  localitySearchResults,
                  Time.UNLIMITED,
                  'CACHE'
                )
                .pipe(take(1))
                .subscribe();
              return localitySearchResults;
            }),
            catchError((error) => {
              this.logs.error('LocalitySearchService', 'Google Autocomplete error', error);
              return of(null);
            })
          );
        }
      })
    );
  }
}
